const nameOf = (item, isRTL) => {
  if (!item) return "";
  if (isRTL) {
    return item.name_ar || item.name_en || "";
  }
  return item.name_en || item.name_ar || "";
};

const lookupNames = (ids, options, getId, isRTL) => {
  if (!ids?.length) return "";
  const mapping = new Map(
    (options || []).map((item) => [String(getId(item)), nameOf(item, isRTL)])
  );
  return ids
    .map((id) => mapping.get(String(id)))
    .filter(Boolean)
    .join(" - ");
};

const lookupUserNames = (ids, users, isRTL) => {
  if (!ids?.length) return "";
  const mapping = new Map(
    (users || []).map((user) => [
      String(user.id),
      isRTL
        ? user.arabic_name || user.english_name || user.username
        : user.english_name || user.arabic_name || user.username,
    ])
  );
  return ids
    .map((id) => mapping.get(String(id)))
    .filter(Boolean)
    .join(" - ");
};

const formatDisplayDate = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (raw.length >= 10 && raw[4] === "-" && raw[7] === "-") {
    const datePart = raw.slice(0, 10);
    const timePart = raw.length >= 16 ? raw.slice(11, 16) : "";
    const formatted = `${datePart.slice(8, 10)}/${datePart.slice(5, 7)}/${datePart.slice(0, 4)}`;
    return timePart ? `${formatted} ${timePart}` : formatted;
  }
  return raw;
};

const classificationLabel = (value, t) => {
  const key = {
    0: "complaintClassification.unclassified",
    1: "complaintClassification.valid",
    2: "complaintClassification.malicious",
  }[Number(value)];
  return key ? t(key) : String(value);
};

/**
 * Convert ticket search/report filters into readable bilingual lines.
 * @returns {{ label: string, value: string }[]}
 */
export const buildFiltersSummary = (
  filters,
  t,
  referenceData = {},
  userOptions = [],
  language = "ar"
) => {
  const isRTL = !String(language || "ar").toLowerCase().startsWith("en");
  const lines = [];

  const add = (label, value) => {
    if (!value) return;
    lines.push({ label, value });
  };

  if (filters.q?.trim()) {
    add(t("search.searchPlaceholder"), filters.q.trim());
  }
  if (filters.status && filters.status !== "all") {
    add(
      t("search.filterStatus"),
      t(`search.status.${filters.status}`, { defaultValue: filters.status })
    );
  }
  add(
    t("search.filterComplaintClassification"),
    (filters.complaintClassification || [])
      .map((id) => classificationLabel(id, t))
      .join(" - ")
  );
  add(
    t("search.filterContactReason"),
    lookupNames(
      filters.contactReasons,
      referenceData.contactReasons,
      (item) => item.reason_id ?? item.contact_reason_id ?? item.id,
      isRTL
    )
  );
  add(
    t("search.filterCommunicationMethod"),
    lookupNames(
      filters.communicationMethods,
      referenceData.communicationMethods,
      (item) => item.method_id ?? item.communication_method_id ?? item.id,
      isRTL
    )
  );
  add(
    t("search.filterDepartments"),
    lookupNames(
      filters.departments,
      referenceData.departments,
      (item) => item.department_id ?? item.id,
      isRTL
    )
  );
  add(
    t("search.filterBranches"),
    lookupNames(
      filters.branches,
      referenceData.branches,
      (item) => item.branch_id ?? item.id,
      isRTL
    )
  );
  add(
    t("search.filterProducts"),
    lookupNames(
      filters.products,
      referenceData.products,
      (item) => item.product_id ?? item.id,
      isRTL
    )
  );
  add(t("search.filterCreatedFrom"), formatDisplayDate(filters.createdFrom));
  add(t("search.filterCreatedTo"), formatDisplayDate(filters.createdTo));
  add(t("search.filterUpdatedFrom"), formatDisplayDate(filters.updatedFrom));
  add(t("search.filterUpdatedTo"), formatDisplayDate(filters.updatedTo));
  add(
    t("search.filterCreators"),
    lookupUserNames(filters.creators, userOptions, isRTL)
  );
  add(
    t("search.filterParticipants"),
    lookupUserNames(filters.participants, userOptions, isRTL)
  );

  return lines;
};

export const buildActionsFiltersSummary = (filters, t, actionTypes, language = "ar") => {
  const isRTL = !String(language || "ar").toLowerCase().startsWith("en");
  const lines = [];
  const add = (label, value) => {
    if (!value) return;
    lines.push({ label, value });
  };

  add(t("actionsReport.ticketFrom"), formatDisplayDate(filters.ticketFrom));
  add(t("actionsReport.ticketTo"), formatDisplayDate(filters.ticketTo));
  add(t("actionsReport.actionFrom"), formatDisplayDate(filters.actionFrom));
  add(t("actionsReport.actionTo"), formatDisplayDate(filters.actionTo));
  add(
    t("actionsReport.actionTypes"),
    lookupNames(
      filters.actionTypes,
      actionTypes,
      (item) => item.action_type_id,
      isRTL
    )
  );
  return lines;
};
